import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const getAuthToken = () => {
    const adminData = sessionStorage.getItem('adminkhadamat');
    if (adminData) {
        try {
            const parsed = JSON.parse(adminData);
            // البحث عن التوكن في أماكن مختلفة
            if (parsed.data?.token?.access_token) {
              const token = String(parsed.data.token.access_token);
              return token;
            }
            if (parsed.data?.access_token) return parsed.data.access_token;
            if (parsed.data?.token) return parsed.data.token;
            if (parsed.token) return parsed.token;
            if (parsed.result?.token) return parsed.result.token;
            if (parsed.user?.token) return parsed.user.token;
            return null;
        } catch {
            return null;
        }
    }
    return null;
};

// Fetch all deliveries
export const fetchDeliveries = createAsyncThunk(
    'adminDeliveries/fetchDeliveries',
    async ({ page = 1, name = '', phone = '', status = '', ban = '' }, { rejectWithValue }) => {
        try {
            const token = getAuthToken();
            const params = { page };
            if (name) params.name = name;
            if (phone) params.phone = phone;
            if (status !== '') params.status = status;
            if (ban !== '') params.ban = ban;

            const response = await axios.get(`${BASE_URL}/admin/deliveries`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
                params
            });

            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'فشل في جلب الدليفري');
        }
    }
);

// Fetch delivery details
export const fetchDeliveryDetails = createAsyncThunk(
    'adminDeliveries/fetchDeliveryDetails',
    async (deliveryId, { rejectWithValue }) => {
        try {
            const token = getAuthToken();
            const response = await axios.get(`${BASE_URL}/admin/deliveries/${deliveryId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });

            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'فشل في جلب تفاصيل الدليفري');
        }
    }
);

// Toggle delivery status
export const toggleDeliveryStatus = createAsyncThunk(
    'adminDeliveries/toggleDeliveryStatus',
    async ({ deliveryId, status }, { rejectWithValue }) => {
        try {
            const token = getAuthToken();
            const response = await axios.post(
                `${BASE_URL}/admin/deliveries/${deliveryId}/status?status=${status}`,
                {},
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept-Language': 'ar',
                        'lang': 'ar',
                    },
                }
            );

            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'فشل في العملية');
        }
    }
);

// Toggle delivery ban
export const toggleDeliveryBan = createAsyncThunk(
    'adminDeliveries/toggleDeliveryBan',
    async ({ deliveryId, status }, { rejectWithValue }) => {
        try {
            const token = getAuthToken();
            const response = await axios.post(
                `${BASE_URL}/admin/deliveries/${deliveryId}/ban?status=${status}`,
                {},
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept-Language': 'ar',
                        'lang': 'ar',
                    },
                }
            );

            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'فشل في العملية');
        }
    }
);

const adminDeliveriesSlice = createSlice({
    name: 'adminDeliveries',
    initialState: {
        deliveries: [],
        deliveryDetails: null,
        pagination: null,
        loading: false,
        detailsLoading: false,
        updating: false,
        error: null,
        success: null,
    },
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccess: (state) => {
            state.success = null;
        },
        clearDeliveryDetails: (state) => {
            state.deliveryDetails = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch Deliveries
            .addCase(fetchDeliveries.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchDeliveries.fulfilled, (state, action) => {
                state.loading = false;
                const deliveriesData = action.payload.data?.data || action.payload.data;
                state.deliveries = Array.isArray(deliveriesData) ? deliveriesData : [];
                state.pagination = action.payload.data?.meta || action.payload.pagination;
            })
            .addCase(fetchDeliveries.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Fetch Delivery Details
            .addCase(fetchDeliveryDetails.pending, (state) => {
                state.detailsLoading = true;
                state.error = null;
            })
            .addCase(fetchDeliveryDetails.fulfilled, (state, action) => {
                state.detailsLoading = false;
                state.deliveryDetails = action.payload.data;
            })
            .addCase(fetchDeliveryDetails.rejected, (state, action) => {
                state.detailsLoading = false;
                state.error = action.payload;
            })

            // Toggle Status
            .addCase(toggleDeliveryStatus.pending, (state) => {
                state.updating = true;
                state.error = null;
            })
            .addCase(toggleDeliveryStatus.fulfilled, (state, action) => {
                state.updating = false;
                state.success = action.payload.message || 'تم تحديث الحالة بنجاح';
            })
            .addCase(toggleDeliveryStatus.rejected, (state, action) => {
                state.updating = false;
                state.error = action.payload;
            })

            // Toggle Ban
            .addCase(toggleDeliveryBan.pending, (state) => {
                state.updating = true;
                state.error = null;
            })
            .addCase(toggleDeliveryBan.fulfilled, (state, action) => {
                state.updating = false;
                state.success = action.payload.message || 'تم تحديث حالة الحظر بنجاح';
            })
            .addCase(toggleDeliveryBan.rejected, (state, action) => {
                state.updating = false;
                state.error = action.payload;
            });
    },
});

export const { clearError, clearSuccess, clearDeliveryDetails } = adminDeliveriesSlice.actions;
export default adminDeliveriesSlice.reducer;

